import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      console.log('User not admin, redirecting...', { user });
      navigate('/');
      return;
    }
    if (!token) {
      console.log('No token found, redirecting...');
      navigate('/login');
      return;
    }
    fetchStats();
  }, [user, token, navigate]);

  const fetchStats = async () => {
    try {
      console.log('Fetching stats with token:', token?.substring(0, 20) + '...');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/admin/stats`, config);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401) {
        console.log('Unauthorized - redirecting to login');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-4 md:px-6 lg:px-8 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 md:mt-2 text-sm text-gray-600">Welcome back, {user?.full_name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-4 mb-6 md:mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 md:block hidden">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div className="md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Products</dt>
                    <dd className="text-2xl md:text-3xl font-semibold text-gray-900">{stats?.total_products || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 md:block hidden">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div className="md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Orders</dt>
                    <dd className="text-2xl md:text-3xl font-semibold text-gray-900">{stats?.total_orders || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 md:block hidden">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Users</dt>
                    <dd className="text-2xl md:text-3xl font-semibold text-gray-900">{stats?.total_users || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 md:block hidden">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Revenue</dt>
                    <dd className="text-xl md:text-3xl font-semibold text-gray-900">₹{stats?.total_revenue?.toLocaleString() || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg p-4 md:p-6">
          <h2 className="text-base md:text-lg font-medium text-gray-900 mb-3 md:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-2 lg:grid-cols-4 md:gap-4">
            <button
              onClick={() => navigate('/admin/products')}
              className="inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 border border-transparent text-xs md:text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Manage Products
            </button>
            <button
              onClick={() => navigate('/admin/orders')}
              className="inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 border border-transparent text-xs md:text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              View Orders
            </button>
            <button
              onClick={() => navigate('/admin/users')}
              className="inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 border border-transparent text-xs md:text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
            >
              Manage Users
            </button>
            <button
              onClick={() => navigate('/admin/cache')}
              className="inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 border border-transparent text-xs md:text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
            >
              Cache
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
